const API_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

export const money = (value) =>
  Number(value || 0).toLocaleString("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  });

const toSafeDate = (value) => {
  if (!value) return null;

  /*
   * Évite un éventuel décalage de jour pour les DATEONLY MySQL.
   * 2026-07-16 devient une date locale à midi.
   */
  if (
    typeof value === "string" &&
    /^\d{4}-\d{2}-\d{2}$/.test(value.slice(0, 10))
  ) {
    const [year, month, day] = value.slice(0, 10).split("-").map(Number);

    const date = new Date(year, month - 1, day, 12, 0, 0);

    return Number.isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
};

export const fmtDate = (value) => {
  const date = toSafeDate(value);

  if (!date) return "—";

  return new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "medium",
  }).format(date);
};

export const fmtDateTime = (value) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

export const getClaimantLabel = (claim) => {
  const prenom = claim?.Claimant?.prenom || "";
  const nom = claim?.Claimant?.nom || "";
  const email = claim?.Claimant?.email || "";

  const fullName = `${prenom} ${nom}`.trim();

  return fullName || email || `Utilisateur #${claim?.user_id || "?"}`;
};

export const getClaimantInitials = (claim) => {
  const label = getClaimantLabel(claim);

  return label
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("");
};

export const isManualExpenseClaim = (claim) =>
  claim?.event_mode === "manual";

const getCalendarEvent = (claim, eventDetailsMap = {}) => {
  return (
    eventDetailsMap?.[claim?.event_id] ||
    claim?.ClaimEvent ||
    null
  );
};

export const getEventLabel = (claim, eventDetailsMap = {}) => {
  if (isManualExpenseClaim(claim)) {
    return (
      claim?.titre_event ||
      "Réunion ou événement hors calendrier"
    );
  }

  const event = getCalendarEvent(claim, eventDetailsMap);

  return (
    event?.titre ||
    event?.nom_event ||
    event?.nom ||
    event?.title ||
    claim?.titre_event ||
    "Événement"
  );
};

export const getEventDatePlace = (claim, eventDetailsMap = {}) => {
  let dateValue = null;
  let placeValue = "";

  if (isManualExpenseClaim(claim)) {
    dateValue = claim?.start_event || null;
    placeValue = claim?.lieu_event || "";
  } else {
    const event = getCalendarEvent(claim, eventDetailsMap);

    dateValue =
      event?.start ||
      event?.date_event ||
      claim?.start_event ||
      null;

    placeValue =
      event?.lieu_event ||
      claim?.lieu_event ||
      "";
  }

  const parts = [
    dateValue ? fmtDate(dateValue) : null,
    placeValue || null,
  ].filter(Boolean);

  return parts.join(" · ");
};

export const getEventMeta = (claim, eventDetailsMap = {}) => {
  if (isManualExpenseClaim(claim)) {
    return "Saisie manuelle · Hors calendrier";
  }

  const event = getCalendarEvent(claim, eventDetailsMap);

  if (!event) return "";

  const parts = [
    event.type_event,
    event.level_event,
    event.cate_event,
    event.organisateur,
  ].filter(Boolean);

  return parts.join(" · ");
};

export const getBudgetCodeLabel = (claim) => {
  if (!claim?.BudgetCode) return "—";

  return `${claim.BudgetCode.code} · ${claim.BudgetCode.libelle}`;
};

export const statusLabel = (status) => {
  switch (status) {
    case "draft":
      return "Brouillon";

    case "submitted":
      return "Soumise";

    case "approved":
      return "Approuvée";

    case "rejected":
      return "Rejetée";

    case "paid":
      return "Payée";

    default:
      return status || "—";
  }
};

export const statusClasses = (status) => {
  switch (status) {
    case "draft":
      return "bg-slate-100 text-slate-700 ring-slate-200";

    case "submitted":
      return "bg-amber-100 text-amber-800 ring-amber-200";

    case "approved":
      return "bg-emerald-100 text-emerald-800 ring-emerald-200";

    case "rejected":
      return "bg-rose-100 text-rose-700 ring-rose-200";

    case "paid":
      return "bg-sky-100 text-sky-700 ring-sky-200";

    default:
      return "bg-slate-100 text-slate-700 ring-slate-200";
  }
};

export const toAbsoluteFileUrl = (url) => {
  if (!url) return "#";

  if (/^https?:\/\//i.test(url)) {
    return url;
  }

  return `${API_BASE}${url.startsWith("/") ? "" : "/"}${url}`;
};

export const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");

  anchor.href = url;
  anchor.download = filename;

  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();

  window.URL.revokeObjectURL(url);
};

export const cleanFilters = (filters) =>
  Object.fromEntries(
    Object.entries(filters).filter(
      ([, value]) =>
        value !== "" &&
        value !== null &&
        value !== undefined,
    ),
  );

const getSortValue = (claim, sortBy) => {
  switch (sortBy) {
    case "id":
      return Number(claim?.id || 0);

    case "claimant":
      return getClaimantLabel(claim).toLowerCase();

    case "event":
      return getEventLabel(claim).toLowerCase();

    case "budget":
      return getBudgetCodeLabel(claim).toLowerCase();

    case "status":
      return String(claim?.statut || "").toLowerCase();

    case "total_frais":
      return Number(claim?.total_frais || 0);

    case "total_general":
      return Number(claim?.total_general || 0);

    case "date_soumission":
      return claim?.date_soumission
        ? new Date(claim.date_soumission).getTime()
        : 0;

    case "created_at":
    default:
      return claim?.created_at
        ? new Date(claim.created_at).getTime()
        : 0;
  }
};

export const sortClaims = (
  claims,
  sortBy = "created_at",
  sortDir = "desc",
) => {
  const rows = [...(claims || [])];

  rows.sort((a, b) => {
    const valueA = getSortValue(a, sortBy);
    const valueB = getSortValue(b, sortBy);

    if (
      typeof valueA === "string" &&
      typeof valueB === "string"
    ) {
      return sortDir === "asc"
        ? valueA.localeCompare(valueB, "fr")
        : valueB.localeCompare(valueA, "fr");
    }

    return sortDir === "asc"
      ? valueA - valueB
      : valueB - valueA;
  });

  return rows;
};